/*
 * Angular 2 decorators and services
 */
import { Component, Inject, OnInit, DOCUMENT } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { Router, NavigationEnd, ActivatedRoute } from "@angular/router";
import { Account } from "./shared/types/account.type";
import { AccountService } from "./shared/services/account.service";
import { AppState } from "./app.service";
import { TranslateService } from "@ngx-translate/core";
import { AppTranslateService } from "./shared/services/app-translate.service";
import { Language } from "./shared/types/language.type";
import { AppStateType } from "./shared/types/app-state-type";
import { environment } from "../environments/environment";
import { LANGUAGES } from "./languages";
import * as Sentry from "@sentry/angular";
import { GoogleAnalyticsService } from "./shared/services/google-analytics.service";
import { ErrorReportingService } from "./shared/services/error-reporting.service";
import { ToastProperties, ToastService } from "./shared/services/toast.service";
import { BrazeService } from "./shared/services/braze.service";
import { SupportedLanguage, ToastState } from "@cariloop/ui-components";
import { delay, tap } from "rxjs/operators";
import { CookieService } from "./shared/services/cookie.service";
import { AnalyticsService } from "@cariloop/analytics";

/*
 * App Component
 * Top Level Component
 */
@Component({
  selector: "app",
  styleUrls: ["./app.component.scss"],
  templateUrl: "app.component.html",
  standalone: false,
})
export class AppComponent implements OnInit {
  public isLogged: boolean;
  public loggedUser: Account;
  public isMobile = false;
  public availableLangs: Array<Language> = [];
  public rootName = "";

  public toastProperties: ToastProperties = {
    toastMessage: "",
    showToast: false,
    toastType: ToastState.success,
  };
  private toastTimer: number;

  constructor(
    @Inject(DOCUMENT) private _document: Document,
    private router: Router,
    private route: ActivatedRoute,
    private appState: AppState,
    private accountService: AccountService,
    private titleService: Title,
    private translate: TranslateService,
    private appTranslate: AppTranslateService,
    private errorReportingService: ErrorReportingService,
    private gaService: GoogleAnalyticsService,
    private toastService: ToastService,
    private cookieService: CookieService,
    private brazeService: BrazeService,
    private analyticsService: AnalyticsService,
  ) {
    this.errorSubscription();
    this.languageSubscription();
    this.setRaygunContext();
    this.setLanguage();
    this.listenToastEvents();
    this.brazeService.init();
  }

  public ngOnInit() {
    if (window.innerWidth < 700) {
      this.isMobile = true;
    }
    this.routerSubscription();
    this.appStateSubscription();
    this.isLogged = this.checkIfLogged();
    if (this.isLogged) {
      this.identifyUser();
    }
    this.cookieService.loadCookieScript();
  }

  public errorSubscription(): void {
    this.appState.onStatusChange().subscribe((status) => {
      if (status === "inactive") {
        this.confirmServerStatus();
      } else if (status === "active") {
        console.log("Server status: Ok");
      }
    });
  }

  public setLanguage(): void {
    const currentUser = this.accountService.getLogged();
    const memoryLang = this.accountService.getLocalLanguage();
    let currentLang: string;
    this.translate.setFallbackLang(SupportedLanguage.EN);
    if (memoryLang && memoryLang.value) {
      currentLang = memoryLang.value;
    } else {
      currentLang = currentLang =
        !(currentUser instanceof Error) && currentUser.language
          ? currentUser.language
          : this.translate.getBrowserLang();
    }
    this.switchLanguage(currentLang as SupportedLanguage);
  }

  public languageSubscription(): void {
    this.appTranslate.onLangChange().subscribe((lang) => {
      this.switchLanguage(lang as SupportedLanguage);
    });
  }

  confirmServerStatus(): void {
    setTimeout(() => {
      this.validateStatus();
    }, 500);
  }

  private validateStatus(): void {
    this.accountService.getHeartbeat().subscribe(
      (res) => {
        console.log("Server status: Ok");
      },
      (err) => {
        if (err.status === 0 || err.status >= 503) {
          this.router.navigate(["/disconnection"]);
        } else {
          this.appState.status({ status: 200 });
          this.router.navigate(["/login"]);
        }
      },
    );
  }

  public filterLanguages(
    currentLang: string,
    langList: Array<Language>,
  ): Array<Language> {
    return langList.filter((lang) => {
      return lang.value !== currentLang;
    });
  }

  public switchLanguage(language = SupportedLanguage.EN): void {
    const isSupportedLang = LANGUAGES.some((lang) => lang.value == language);
    if (!isSupportedLang) {
      language = SupportedLanguage.EN;
    }
    this.availableLangs = LANGUAGES;
    this._document.documentElement.lang = language;
    this.translate.use(language);
  }

  public routerSubscription(): void {
    this.router.events.subscribe((event: any) => {
      this.isLogged = this.checkIfLogged();
      if (event instanceof NavigationEnd) {
        let excludeEnv = ["local", "bizdev", "alpha", "beta"];
        if (!excludeEnv.includes(environment.envName)) {
          // Google analytics User navigation tracking
          const path = this.getCurrentPath(this.route).replace(
            /\:[a-zA-Z0-9]+/g,
            "*",
          );
          this.gaService.pageView(path);
        }
      }
    });
  }

  public appStateSubscription(): void {
    this.appState.onAppStateUpdate().subscribe((update) => {
      if (update) {
        this.manageAppState(update);
      }
    });
  }

  private getCurrentPath(route: ActivatedRoute): string {
    const path =
      route.routeConfig && route.routeConfig.path
        ? "/" + route.routeConfig.path
        : "";
    if (route.children && route.children.length > 0) {
      return path + this.getCurrentPath(route.children[0]);
    } else {
      return path;
    }
  }

  manageAppState(state: AppStateType) {
    this.rootName = state.path.root;
    this.isLogged = this.checkIfLogged();
    this.titleService.setTitle("Cariloop");
  }

  public setRaygunContext(): void {
    this.isLogged = this.checkIfLogged();
    if (!this.isLogged) {
      return;
    }
    Sentry.setUser({
      id: this.loggedUser.id,
      isAnonymous: false,
      organizationId: this.loggedUser.organizationId,
    });
  }

  public onResize(event) {
    this.isMobile = event.target.innerWidth < 700;
  }

  private checkIfLogged() {
    const getLoggedResult = this.accountService.getLogged();
    if (getLoggedResult instanceof Error) {
      return false;
    }

    this.loggedUser = getLoggedResult;
    return true;
  }

  private errorHandler(err) {
    this.appState.status(err);
    if (err.status === 401) {
      this.accountService.cleanLocalStorage();
      this.router.navigate(["/login"]);
    }
    console.log(err);
    this.errorReportingService.send(err);
  }

  private identifyUser() {
    this.analyticsService.identifyUser(this.loggedUser.id);
  }

  listenToastEvents() {
    this.toastService
      .listenToastChange()
      .pipe(
        tap((_) => {
          this.toastProperties.showToast = false;
          if (this.toastTimer) {
            clearTimeout(this.toastTimer);
          }
        }),
        delay(500),
      )
      .subscribe((properties: ToastProperties) => {
        this.toastProperties = properties;
        this.toastTimer = setTimeout(() => {
          this.toastProperties.showToast = false;
        }, properties.toastDuration ?? 5000);
      });
  }
}
